import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  RotateCcw, ArrowLeft, Loader2, AlertTriangle, FileText,
  Truck, CheckCircle2, XCircle, Clock, Package,
  CreditCard, ShoppingCart, Image, MessageSquare,
} from 'lucide-react';
import { api } from '@/utils/api';

// ─── Types ───────────────────────────────────────────────────────────────────

interface VRItem {
  id: string;
  productId: string;
  productName: string;
  sku: string;
  qty: number;
  unit: string;
  rate: number;
  reason: string;
}

interface DebitNote {
  id: string;
  dnNumber: string;
  amount: number;
  status: string;
}

interface ReplacementPO {
  id: string;
  poNumber: string;
  status: string;
  totalAmount: number;
}

interface VendorReturn {
  id: string;
  number: string;
  vendorId: string;
  vendorName: string;
  type: string;
  status: string;
  totalAmount: number;
  notes: string | null;
  returnTrackingNumber: string | null;
  vendorRefNumber: string | null;
  debitNoteId: string | null;
  debitNote?: DebitNote | null;
  replacementPOId: string | null;
  replacementPO?: ReplacementPO | null;
  items: VRItem[];
  photos: string[];
  createdAt: string;
  updatedAt: string;
  requestedBy: string;
}

// ─── Status Config ───────────────────────────────────────────────────────────

const STATUS_STYLES: Record<string, string> = {
  draft: 'bg-gray-500/20 text-gray-400',
  pending_approval: 'bg-yellow-500/20 text-yellow-400',
  approved: 'bg-blue-500/20 text-blue-400',
  rejected: 'bg-red-500/20 text-red-400',
  in_transit: 'bg-purple-500/20 text-purple-400',
  received_by_vendor: 'bg-indigo-500/20 text-indigo-400',
  credit_received: 'bg-emerald-500/20 text-emerald-400',
  replacement_ordered: 'bg-cyan-500/20 text-cyan-400',
  closed: 'bg-emerald-500/20 text-emerald-400',
  cancelled: 'bg-red-500/20 text-red-400',
};

const STATUS_FLOW: Record<string, { label: string; icon: typeof Clock; color: string }> = {
  draft: { label: 'Draft', icon: FileText, color: 'text-gray-400' },
  pending_approval: { label: 'Pending Approval', icon: Clock, color: 'text-yellow-400' },
  approved: { label: 'Approved', icon: CheckCircle2, color: 'text-blue-400' },
  in_transit: { label: 'In Transit', icon: Truck, color: 'text-purple-400' },
  received_by_vendor: { label: 'Received by Vendor', icon: Package, color: 'text-indigo-400' },
  credit_received: { label: 'Credit Received', icon: CreditCard, color: 'text-emerald-400' },
  replacement_ordered: { label: 'Replacement Ordered', icon: ShoppingCart, color: 'text-cyan-400' },
  closed: { label: 'Closed', icon: CheckCircle2, color: 'text-emerald-400' },
  cancelled: { label: 'Cancelled', icon: XCircle, color: 'text-red-400' },
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium capitalize ${STATUS_STYLES[status] || 'bg-gray-500/20 text-gray-400'}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

function formatINR(value: number): string {
  const abs = Math.abs(value);
  const [intPart, decPart] = abs.toFixed(2).split('.');
  let lastThree = intPart.slice(-3);
  const remaining = intPart.slice(0, -3);
  if (remaining.length > 0) {
    lastThree = remaining.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + lastThree;
  }
  return `${value < 0 ? '-' : ''}₹${lastThree}.${decPart}`;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function VendorReturnDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [vr, setVr] = useState<VendorReturn | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Action modals
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [trackingInput, setTrackingInput] = useState('');
  const [showVendorRefModal, setShowVendorRefModal] = useState(false);
  const [vendorRefInput, setVendorRefInput] = useState('');
  const [showReplacementModal, setShowReplacementModal] = useState(false);
  const [replacementPOIdInput, setReplacementPOIdInput] = useState('');

  const fetchReturn = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/vendor-returns/${id}`);
      setVr(data.data);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to load vendor return');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchReturn(); }, [fetchReturn]);

  const performAction = async (action: string, payload?: Record<string, any>) => {
    if (!vr) return;
    setActionLoading(true);
    try {
      const { data } = await api.post(`/vendor-returns/${vr.id}/${action}`, payload || {});
      setVr(data.data);
    } catch (e: any) {
      alert(e?.response?.data?.error || `Failed to ${action.replace(/-/g, ' ')}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDispatch = () => {
    setShowTrackingModal(true);
  };

  const confirmDispatch = async () => {
    await performAction('dispatch', { returnTrackingNumber: trackingInput });
    setShowTrackingModal(false);
    setTrackingInput('');
  };

  const handleVendorReceived = () => {
    setShowVendorRefModal(true);
  };

  const confirmVendorReceived = async () => {
    await performAction('vendor-received', { vendorRefNumber: vendorRefInput });
    setShowVendorRefModal(false);
    setVendorRefInput('');
  };

  if (loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--saffron-600)]" />
      </div>
    );
  }

  if (error || !vr) {
    return (
      <div className="mx-auto mt-20 max-w-md rounded-lg border border-red-500/30 bg-red-500/10 p-6 text-center">
        <AlertTriangle className="mx-auto mb-2 h-8 w-8 text-red-400" />
        <p className="text-sm text-red-300">{error || 'Vendor return not found'}</p>
        <button
          onClick={() => { window.location.href = '/vendor-returns'; }}
          className="mt-3 rounded bg-[var(--ink-0)] px-4 py-1.5 text-sm text-white hover:bg-[var(--ink-0)]"
        >
          Back to List
        </button>
      </div>
    );
  }

  // Timeline from status flow
  const statusOrder = ['draft', 'pending_approval', 'approved', 'in_transit', 'received_by_vendor'];
  const currentIdx = statusOrder.indexOf(vr.status);

  return (
    <div className="space-y-6 p-6">
      {/* Back & Header */}
      <div>
        <button
          onClick={() => { window.location.href = '/vendor-returns'; }}
          className="mb-3 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <ArrowLeft size={16} /> Back to Vendor Returns
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900 dark:text-white">
              <RotateCcw className="h-6 w-6 text-[var(--ink-0)]" />
              {vr.number}
            </h1>
            <div className="mt-1 flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
              <span>{vr.vendorName}</span>
              <span className="capitalize">{vr.type.replace(/_/g, ' ')}</span>
              <span>{new Date(vr.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
            </div>
          </div>
          <StatusBadge status={vr.status} />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2">
        {vr.status === 'draft' && (
          <>
            <button
              onClick={() => { window.location.href = `/vendor-returns/${vr.id}/edit`; }}
              disabled={actionLoading}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Edit
            </button>
            <button
              onClick={() => performAction('approve')}
              disabled={actionLoading}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Approve
            </button>
          </>
        )}
        {vr.status === 'pending_approval' && (
          <button
            onClick={() => performAction('approve')}
            disabled={actionLoading}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            Approve
          </button>
        )}
        {vr.status === 'approved' && (
          <button
            onClick={handleDispatch}
            disabled={actionLoading}
            className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50"
          >
            {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Truck className="h-4 w-4" />}
            Mark Dispatched
          </button>
        )}
        {vr.status === 'in_transit' && (
          <button
            onClick={handleVendorReceived}
            disabled={actionLoading}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Package className="h-4 w-4" />}
            Mark Vendor Received
          </button>
        )}
        {vr.status === 'received_by_vendor' && (
          <>
            <button
              onClick={() => performAction('credit-received')}
              disabled={actionLoading}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              <CreditCard className="h-4 w-4" /> Credit Received
            </button>
            <button
              onClick={() => setShowReplacementModal(true)}
              disabled={actionLoading}
              className="inline-flex items-center gap-2 rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:opacity-50"
            >
              <ShoppingCart className="h-4 w-4" /> Replacement Ordered
            </button>
          </>
        )}
        {(vr.status === 'credit_received' || vr.status === 'replacement_ordered') && (
          <button
            onClick={() => performAction('close')}
            disabled={actionLoading}
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            <CheckCircle2 className="h-4 w-4" /> Close
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Main Content — 2 cols */}
        <div className="space-y-6 lg:col-span-2">
          {/* Items Table */}
          <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
            <div className="border-b border-gray-200 px-5 py-4 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Return Items</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                  <tr className="text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    <th className="px-5 py-3">Product</th>
                    <th className="px-5 py-3">SKU</th>
                    <th className="px-5 py-3 text-right">Qty</th>
                    <th className="px-5 py-3">Unit</th>
                    <th className="px-5 py-3 text-right">Rate</th>
                    <th className="px-5 py-3 text-right">Total</th>
                    <th className="px-5 py-3">Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                  {vr.items.map((item) => (
                    <tr key={item.id}>
                      <td className="px-5 py-3 font-medium text-gray-900 dark:text-white">{item.productName}</td>
                      <td className="px-5 py-3 text-sm text-gray-500">{item.sku}</td>
                      <td className="px-5 py-3 text-right text-gray-900 dark:text-white">{item.qty}</td>
                      <td className="px-5 py-3 text-sm text-gray-500">{item.unit}</td>
                      <td className="px-5 py-3 text-right text-gray-900 dark:text-white">{formatINR(item.rate)}</td>
                      <td className="px-5 py-3 text-right font-medium text-gray-900 dark:text-white">{formatINR(item.qty * item.rate)}</td>
                      <td className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">{item.reason || '—'}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-300 dark:border-gray-600">
                    <td colSpan={5} className="px-5 py-3 text-right text-sm font-semibold text-gray-700 dark:text-gray-300">Total</td>
                    <td className="px-5 py-3 text-right text-lg font-bold text-gray-900 dark:text-white">{formatINR(vr.totalAmount)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Photos */}
          {vr.photos && vr.photos.length > 0 && (
            <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                <Image className="h-4 w-4" /> Photos
              </h3>
              <div className="grid grid-cols-4 gap-3">
                {vr.photos.map((url, idx) => (
                  <img key={idx} src={url} alt={`Photo ${idx + 1}`} className="h-32 w-full rounded-lg border border-gray-200 object-cover dark:border-gray-600" />
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {vr.notes && (
            <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
              <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                <MessageSquare className="h-4 w-4" /> Notes
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{vr.notes}</p>
            </div>
          )}
        </div>

        {/* Sidebar — 1 col */}
        <div className="space-y-4">
          {/* Timeline */}
          <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
            <h3 className="mb-4 text-sm font-semibold text-gray-900 dark:text-white">Status Timeline</h3>
            <div className="space-y-3">
              {statusOrder.map((s, idx) => {
                const config = STATUS_FLOW[s];
                if (!config) return null;
                const Icon = config.icon;
                const isActive = idx <= currentIdx;
                const isCurrent = s === vr.status;
                return (
                  <div key={s} className="flex items-start gap-3">
                    <div className={`mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ${isCurrent ? 'bg-[var(--ink-0)] text-white' : isActive ? 'bg-gray-300 dark:bg-gray-600' : 'bg-gray-100 dark:bg-gray-700'}`}>
                      <Icon size={12} className={isCurrent ? 'text-white' : isActive ? 'text-gray-600 dark:text-gray-300' : 'text-gray-400'} />
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${isCurrent ? 'text-[var(--ink-0)]' : isActive ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'}`}>
                        {config.label}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Debit Note Card */}
          {vr.debitNoteId && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 dark:border-emerald-800 dark:bg-emerald-900/20">
              <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-emerald-800 dark:text-emerald-300">
                <CreditCard className="h-4 w-4" /> Linked Debit Note
              </h3>
              <p className="text-lg font-bold text-emerald-900 dark:text-emerald-200">{vr.debitNote?.dnNumber ?? 'N/A'}</p>
              <p className="text-sm text-emerald-700 dark:text-emerald-400">Amount: {vr.debitNote?.amount != null ? formatINR(vr.debitNote.amount) : 'N/A'}</p>
              {vr.debitNote?.status ? <StatusBadge status={vr.debitNote.status} /> : <span className="text-xs text-gray-400">N/A</span>}
            </div>
          )}

          {/* Replacement PO Card */}
          {vr.replacementPOId && (
            <div className="rounded-xl border border-cyan-200 bg-cyan-50 p-5 dark:border-cyan-800 dark:bg-cyan-900/20">
              <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-cyan-800 dark:text-cyan-300">
                <ShoppingCart className="h-4 w-4" /> Replacement PO
              </h3>
              <p className="text-lg font-bold text-cyan-900 dark:text-cyan-200">{vr.replacementPO?.poNumber ?? 'N/A'}</p>
              <p className="text-sm text-cyan-700 dark:text-cyan-400">Amount: {vr.replacementPO?.totalAmount != null ? formatINR(vr.replacementPO.totalAmount) : 'N/A'}</p>
              {vr.replacementPO?.status ? <StatusBadge status={vr.replacementPO.status} /> : <span className="text-xs text-gray-400">N/A</span>}
            </div>
          )}

          {/* Tracking Info */}
          {vr.returnTrackingNumber && (
            <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
              <h3 className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400">Tracking Number</h3>
              <p className="font-mono text-sm font-medium text-gray-900 dark:text-white">{vr.returnTrackingNumber}</p>
            </div>
          )}

          {vr.vendorRefNumber && (
            <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
              <h3 className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400">Vendor Reference</h3>
              <p className="font-mono text-sm font-medium text-gray-900 dark:text-white">{vr.vendorRefNumber}</p>
            </div>
          )}

          {/* Meta Info */}
          <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
            <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">Details</h3>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500 dark:text-gray-400">Created By</dt>
                <dd className="text-gray-900 dark:text-white">{vr.requestedBy}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500 dark:text-gray-400">Created</dt>
                <dd className="text-gray-900 dark:text-white">{new Date(vr.createdAt).toLocaleDateString('en-IN')}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500 dark:text-gray-400">Last Updated</dt>
                <dd className="text-gray-900 dark:text-white">{new Date(vr.updatedAt).toLocaleDateString('en-IN')}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      {/* Tracking Modal */}
      {showTrackingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Enter Tracking Number</h3>
            <input
              type="text"
              value={trackingInput}
              onChange={(e) => setTrackingInput(e.target.value)}
              placeholder="Tracking / AWB number..."
              className="mb-4 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              autoFocus
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowTrackingModal(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={confirmDispatch}
                disabled={actionLoading || !trackingInput.trim()}
                className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50"
              >
                {actionLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                Confirm Dispatch
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Vendor Ref Modal */}
      {showVendorRefModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Enter Vendor Reference</h3>
            <input
              type="text"
              value={vendorRefInput}
              onChange={(e) => setVendorRefInput(e.target.value)}
              placeholder="Vendor acknowledgement reference..."
              className="mb-4 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              autoFocus
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowVendorRefModal(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={confirmVendorReceived}
                disabled={actionLoading || !vendorRefInput.trim()}
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {actionLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                Confirm Received
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Replacement PO Modal */}
      {showReplacementModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Enter Replacement PO ID</h3>
            <input
              type="text"
              value={replacementPOIdInput}
              onChange={(e) => setReplacementPOIdInput(e.target.value)}
              placeholder="Purchase Order ID..."
              className="mb-4 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              autoFocus
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowReplacementModal(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await performAction('replacement-ordered', { replacementPOId: replacementPOIdInput });
                  setShowReplacementModal(false);
                  setReplacementPOIdInput('');
                }}
                disabled={actionLoading || !replacementPOIdInput.trim()}
                className="inline-flex items-center gap-2 rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:opacity-50"
              >
                {actionLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
